import { getDefaultEditorPlaceholder } from '@/lib/editor/defaultEditorPlaceholder';
import Button from '@/lib/vendor/@material-ui/core/src/Button';
import { useForm } from '@tanstack/react-form';
import classNames from 'classnames';
import React from 'react';
import { defineStyles, useStyles } from '../hooks/useStyles';
import { EditorFormComponent, useEditorFormCallbacks } from '../editor/EditorFormComponent';
import { MuiTextField } from '@/components/form-components/MuiTextField';
import { cancelButtonStyles, submitButtonStyles } from '@/components/tanstack-form-components/TanStackSubmit';
import { getUpdatedFieldValues } from '@/components/tanstack-form-components/helpers';
import { useFormErrors } from '@/components/tanstack-form-components/BaseAppForm';
import FormComponentCheckbox from "../form-components/FormComponentCheckbox";
import { useMutation } from "@apollo/client/react";
import { gql } from "@/lib/generated/gql-codegen";

const CollectionsPageFragmentUpdateMutation = gql(`
  mutation updateCollectionCollectionsEditForm($selector: SelectorInput!, $data: UpdateCollectionDataInput!) {
    updateCollection(selector: $selector, data: $data) {
      data {
        ...CollectionsPageFragment
      }
    }
  }
`);

export const styles = defineStyles('CollectionsEditForm', (theme: ThemeType) => ({
  newOrEditForm: {
    maxWidth: 695,
    marginLeft: "auto",
    marginRight: 90,
    padding: 15,
    borderRadius: 2,
    marginBottom: "2em",

    "& form": {
      clear: "both",
      overflow: "auto",
    },
    "& .form-submit": {
      float: "right",
    },
    "& h3": {
      fontSize: "2em",
      marginBottom: "1em",
    },
    "& .input-title input": {
      fontSize: "2em",
    },
  },
  editForm: {
    width: 700,
    marginLeft: "auto",
    marginRight: 75,
  },
  newForm: {
    border: theme.palette.border.normal,
  },
  fieldWrapper: {
    marginTop: 16,
    marginBottom: 16,
  },
  submitButton: submitButtonStyles(theme),
  cancelButton: cancelButtonStyles(theme),
}));

const CollectionsEditForm = ({ initialData, successCallback, cancelCallback }: {
  initialData: UpdateCollectionDataInput & { _id: string },
  successCallback: (doc: CollectionsPageFragment) => void,
  cancelCallback: () => void,
}) => {
  const classes = useStyles(styles);

  const {
    onSubmitCallback,
    onSuccessCallback,
    addOnSubmitCallback,
    addOnSuccessCallback
  } = useEditorFormCallbacks<CollectionsPageFragment>();

  const [mutate] = useMutation(CollectionsPageFragmentUpdateMutation);

  const { setCaughtError, displayedErrorComponent } = useFormErrors();

  const form = useForm({
    defaultValues: {
      ...initialData,
    },
    onSubmit: async ({ value, formApi }) => {
      await onSubmitCallback.current?.();

      try {
        let result: CollectionsPageFragment;

        const updatedFields = getUpdatedFieldValues(formApi, ['contents']);
        const { data } = await mutate({
          variables: {
            selector: { _id: initialData?._id },
            data: updatedFields
          }
        });
        if (!data?.updateCollection?.data) {
          throw new Error('Failed to update collection');
        }
        result = data.updateCollection.data;

        onSuccessCallback.current?.(result);

        successCallback(result);
      } catch (error) {
        setCaughtError(error);
      }
    },
  });

  return (
    <div className={classNames(classes.newOrEditForm, classes.editForm)}>
      <form className="vulcan-form" onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}>
        {displayedErrorComponent}
        <div className={classNames("form-component-EditorFormComponent", classes.fieldWrapper)}>
          <form.Field name="contents">
            {(field) => (
              <EditorFormComponent
                field={field}
                name="contents"
                formType='edit'
                document={form.state.values}
                addOnSubmitCallback={addOnSubmitCallback}
                addOnSuccessCallback={addOnSuccessCallback}
                hintText={getDefaultEditorPlaceholder()}
                fieldName="contents"
                collectionName="Collections"
                commentEditor={false}
                commentStyles={false}
                hideControls={false}
              />
            )}
          </form.Field>
        </div>

        <div className={classNames('input-title',classes.fieldWrapper)}>
          <form.Field name="title">
            {(field) => (
              <MuiTextField
                field={field}
                label="Title"
              />
            )}
          </form.Field>
        </div>

        <div className={classes.fieldWrapper}>
          <form.Field name="slug">
            {(field) => (
              <MuiTextField
                field={field}
                label="Slug"
              />
            )}
          </form.Field>
        </div>

        <div className={classes.fieldWrapper}>
          <form.Field name="gridImageId">
            {(field) => (
              <MuiTextField
                field={field}
                label="Grid image ID"
              />
            )}
          </form.Field>
        </div>

        <div className={classes.fieldWrapper}>
          <form.Field name="firstPageLink">
            {(field) => (
              <MuiTextField
                field={field}
                label="First page link"
              />
            )}
          </form.Field>
        </div>

        <div className={classes.fieldWrapper}>
          <form.Field name="hideStartReadingButton">
            {(field) => (
              <FormComponentCheckbox
                field={field}
                label="Hide start reading button"
              />
            )}
          </form.Field>
        </div>

        <div className={classes.fieldWrapper}>
          <form.Field name="noindex">
            {(field) => (
              <FormComponentCheckbox
                field={field}
                label="Noindex"
              />
            )}
          </form.Field>
        </div>

        <div className="form-submit">
          <Button
            className={classNames("form-cancel", classes.cancelButton)}
            onClick={(e) => {
              e.preventDefault();
              cancelCallback()
            }}
          >
            Cancel
          </Button>

          <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting]}>
            {([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className={classNames("primary-form-submit-button", classes.submitButton)}
              >
                Submit
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form>
    </div>
  )
}

export default CollectionsEditForm;



